/**
 * CRM Revenue chart helpers: read per-service quoted prices from the latest
 * GeneratedQuote Finance Summary (servicePrice_* inputs / table cells).
 * Used only by the dashboard Revenue widget — does not affect P&L or quotation UI.
 */

const parseAmount = (raw) => {
  const cleaned = String(raw ?? "")
    .replace(/[^\d.-]/g, "")
    .trim();
  if (!cleaned) return 0;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : 0;
};

const stripQuotationVersion = (id) =>
  String(id ?? "")
    .trim()
    .replace(/_v\d+\s*$/i, "");

const getQuotationVersionNum = (quotation) => {
  const id = String(
    quotation?.quotationId ??
      quotation?.QuotationId ??
      quotation?.quotationNumber ??
      ""
  );
  const match = id.match(/_v(\d+)\s*$/i);
  return match ? parseInt(match[1], 10) : 0;
};

const getQuotationSortTime = (quotation) => {
  const t = new Date(
    quotation?.createdAt || quotation?.updatedAt || 0
  ).getTime();
  return Number.isFinite(t) ? t : 0;
};

/** Normalize Redux / API quotation list to an array. */
export function normalizeQuotationsList(quotations) {
  if (Array.isArray(quotations)) return quotations;
  if (Array.isArray(quotations?.data)) return quotations.data;
  if (Array.isArray(quotations?.quotations)) return quotations.quotations;
  return [];
}

const getQuoteHtml = (quotation) =>
  String(
    quotation?.templatedata ||
      quotation?.templateData ||
      quotation?.content ||
      ""
  );

const isGeneratedQuoteRecord = (q) => {
  if (!q) return false;
  const type = String(q.type || "").trim().toLowerCase();
  // Prefer GeneratedQuote; also accept records that clearly carry Finance Summary HTML
  // (some APIs omit/ mistype `type` but still store generated content).
  if (type === "generatedquote") return true;
  if (type === "autosave") return false;
  const html = getQuoteHtml(q);
  return (
    html.includes("servicePrice_") ||
    /finance\s*summary/i.test(html)
  );
};

const extractInputValueAttr = (inputTag) => {
  if (!inputTag) return "";
  const valueMatch = String(inputTag).match(/\bvalue\s*=\s*"([^"]*)"/i);
  if (valueMatch) return valueMatch[1];
  const valueMatch2 = String(inputTag).match(/\bvalue\s*=\s*'([^']*)'/i);
  return valueMatch2 ? valueMatch2[1] : "";
};

/**
 * Parse Finance Summary service → price map from quotation HTML.
 * @param {string} html
 * @returns {Record<string, number>}
 */
export function parseCrmFinanceSummaryServicePrices(html) {
  if (!html || typeof html !== "string") return {};

  const prices = {};

  const addPrice = (serviceType, rawAmount) => {
    const type = String(serviceType || "")
      .replace(/<[^>]+>/g, "")
      .replace(/\s+/g, " ")
      .trim();
    if (!type) return;
    const lower = type.toLowerCase();
    if (
      lower.includes("total quoted") ||
      lower === "total" ||
      lower.startsWith("total ")
    ) {
      return;
    }
    const amount = parseAmount(rawAmount);
    if (!(amount > 0)) return;
    prices[type] = amount;
  };

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");

    // Prefer the Finance Summary table when present.
    let table = null;
    const headings = Array.from(
      doc.querySelectorAll("p, h1, h2, h3, h4, strong, b, span, div")
    );
    for (const node of headings) {
      const title = String(node.textContent || "")
        .trim()
        .toLowerCase();
      if (title !== "finance summary") continue;
      const wrapper = node.parentElement;
      table = wrapper?.querySelector?.("table") || null;
      if (table) break;
      let sibling = node.nextElementSibling;
      while (sibling) {
        if (sibling.tagName === "TABLE") {
          table = sibling;
          break;
        }
        const nested = sibling.querySelector?.("table");
        if (nested) {
          table = nested;
          break;
        }
        sibling = sibling.nextElementSibling;
      }
      if (table) break;
    }

    if (!table) {
      const input = doc.querySelector('input[name^="servicePrice_"]');
      table = input?.closest?.("table") || null;
    }

    // Fall back: any table whose first header cell mentions service type.
    if (!table) {
      const tables = Array.from(doc.querySelectorAll("table"));
      table =
        tables.find((t) => {
          const header = String(t.querySelector("th, td")?.textContent || "")
            .trim()
            .toLowerCase();
          return header.includes("service");
        }) || null;
    }

    if (table) {
      const rows = Array.from(table.querySelectorAll("tr"));
      rows.forEach((tr) => {
        const cells = Array.from(tr.querySelectorAll("td"));
        if (cells.length < 2) return;

        const serviceType = String(cells[0]?.textContent || "")
          .replace(/\s+/g, " ")
          .trim();
        const priceCell = cells[1];
        const input =
          priceCell?.querySelector?.('input[name^="servicePrice_"]') ||
          priceCell?.querySelector?.("input");
        // Prefer input value, but if empty/missing fall back to visible cell text
        // (PDF/HTML often shows "50,000" while value="" if attribute sync failed).
        let raw = "";
        if (input != null) {
          raw = input.getAttribute("value") ?? input.value ?? "";
        }
        if (!parseAmount(raw)) {
          raw = priceCell?.textContent ?? "";
        }
        addPrice(serviceType, raw);
      });
    }

    // Regex fallback when DOM structure is unusual / attributes reordered.
    if (Object.keys(prices).length === 0) {
      const rowRe =
        /<tr[^>]*>\s*<td[^>]*>([\s\S]*?)<\/td>\s*<td[^>]*>([\s\S]*?)<\/td>\s*<\/tr>/gi;
      let match;
      while ((match = rowRe.exec(html)) !== null) {
        const serviceHtml = match[1] || "";
        const priceHtml = match[2] || "";
        const inputTag = (priceHtml.match(/<input\b[^>]*>/i) || [])[0] || "";
        const rawFromInput = extractInputValueAttr(inputTag);
        const raw = rawFromInput || priceHtml.replace(/<[^>]+>/g, " ");
        addPrice(serviceHtml, raw);
      }
    }
  } catch {
    // ignore — return whatever we collected
  }

  return prices;
}

const getQuoteLeadKeys = (q) => {
  const keys = new Set();
  const qid = q?.quotationId ?? q?.QuotationId ?? q?.quotationNumber;
  if (qid != null && String(qid).trim() !== "") {
    const raw = String(qid).trim();
    keys.add(raw);
    keys.add(stripQuotationVersion(raw));
  }
  if (q?.leadid != null && String(q.leadid).trim() !== "") {
    keys.add(String(q.leadid).trim());
  }
  if (q?.leadId != null && String(q.leadId).trim() !== "") {
    keys.add(String(q.leadId).trim());
  }
  return [...keys].filter(Boolean);
};

const getLeadMatchKeys = (lead) => {
  const keys = new Set();
  if (lead?.quotationId != null && String(lead.quotationId).trim() !== "") {
    const raw = String(lead.quotationId).trim();
    keys.add(raw);
    keys.add(stripQuotationVersion(raw));
  }
  if (lead?.id != null) keys.add(String(lead.id).trim());
  if (lead?._id != null) keys.add(String(lead._id).trim());
  return [...keys].filter(Boolean);
};

/**
 * Stable group key so _v1 / _v2 of the same project collapse together.
 */
export function getQuotationLeadGroupKey(quotation) {
  const qid = quotation?.quotationId ?? quotation?.QuotationId ?? quotation?.quotationNumber;
  if (qid != null && String(qid).trim() !== "") {
    return `qid:${stripQuotationVersion(qid)}`;
  }
  if (quotation?.leadid != null && String(quotation.leadid).trim() !== "") {
    return `lead:${String(quotation.leadid).trim()}`;
  }
  if (quotation?.leadId != null && String(quotation.leadId).trim() !== "") {
    return `lead:${String(quotation.leadId).trim()}`;
  }
  if (quotation?._id != null) return `id:${String(quotation._id)}`;
  return null;
}

/**
 * Match GeneratedQuotes for a lead (same keys as Quotation version history).
 */
export function getQuotationsForLead(quotations, lead) {
  const list = normalizeQuotationsList(quotations).filter(isGeneratedQuoteRecord);
  if (list.length === 0 || !lead) return [];

  const leadKeys = new Set(getLeadMatchKeys(lead));
  if (leadKeys.size === 0) return [];

  return list.filter((q) => {
    const quoteKeys = getQuoteLeadKeys(q);
    return quoteKeys.some((k) => leadKeys.has(k));
  });
}

/**
 * Latest GeneratedQuote for a lead (_vN / createdAt).
 */
export function getLatestGeneratedQuotationForLead(quotations, lead) {
  const matched = getQuotationsForLead(quotations, lead);
  if (matched.length === 0) return null;

  return matched.slice().sort((a, b) => {
    const versionDiff = getQuotationVersionNum(b) - getQuotationVersionNum(a);
    if (versionDiff !== 0) return versionDiff;
    return getQuotationSortTime(b) - getQuotationSortTime(a);
  })[0];
}

/**
 * Per-service prices from the latest GeneratedQuote Finance Summary.
 * @returns {{ type: string, amount: number }[] | null}
 */
export function getLeadServicePricesFromLatestQuotation(quotations, lead) {
  const latest = getLatestGeneratedQuotationForLead(quotations, lead);
  if (!latest) return null;

  const priceMap = parseCrmFinanceSummaryServicePrices(getQuoteHtml(latest));
  const entries = Object.entries(priceMap)
    .map(([type, amount]) => ({
      type,
      amount: Number(amount) || 0,
    }))
    .filter((row) => row.type && row.amount > 0);

  return entries.length > 0 ? entries : null;
}

const getQuoteYear = (quotation) => {
  const raw = quotation?.createdAt || quotation?.updatedAt || null;
  if (!raw) return null;
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return null;
  return String(parsed.getFullYear());
};

/**
 * CRM Revenue: sum Finance Summary prices from the latest GeneratedQuote per lead.
 * Does not use Top 10 / equal-split / QuotedValue.
 *
 * @param {object} params
 * @param {array|object} params.quotations
 * @param {array} [params.scopeLeads] optional dashboard-filtered leads; when provided,
 *   only quotations matching those leads are included. When empty/omitted, all quotes count.
 * @param {string} [params.selectedYear] "All Years" or "2026"
 * @returns {{ type: string, amount: number }[]}
 */
export function aggregateCrmRevenueFromLatestQuotations({
  quotations,
  scopeLeads = null,
  selectedYear = "All Years",
} = {}) {
  const list = normalizeQuotationsList(quotations).filter(isGeneratedQuoteRecord);
  if (list.length === 0) return [];

  let scopeKeySet = null;
  if (Array.isArray(scopeLeads) && scopeLeads.length > 0) {
    scopeKeySet = new Set();
    scopeLeads.forEach((lead) => {
      getLeadMatchKeys(lead).forEach((k) => scopeKeySet.add(k));
    });
  }

  // Group by lead/project, keep latest quotation only.
  const latestByLead = new Map();
  list.forEach((q) => {
    const groupKey = getQuotationLeadGroupKey(q);
    if (!groupKey) return;

    if (scopeKeySet) {
      const quoteKeys = getQuoteLeadKeys(q);
      const inScope = quoteKeys.some((k) => scopeKeySet.has(k));
      if (!inScope) return;
    }

    if (selectedYear && selectedYear !== "All Years") {
      const year = getQuoteYear(q);
      // If quote has no date, still include it when year filter is set
      // only when we cannot determine year — prefer include over hiding real data.
      if (year && year !== String(selectedYear)) return;
    }

    const existing = latestByLead.get(groupKey);
    if (!existing) {
      latestByLead.set(groupKey, q);
      return;
    }
    const versionDiff =
      getQuotationVersionNum(q) - getQuotationVersionNum(existing);
    if (versionDiff > 0) {
      latestByLead.set(groupKey, q);
      return;
    }
    if (
      versionDiff === 0 &&
      getQuotationSortTime(q) > getQuotationSortTime(existing)
    ) {
      latestByLead.set(groupKey, q);
    }
  });

  const totals = {};
  latestByLead.forEach((q) => {
    const priceMap = parseCrmFinanceSummaryServicePrices(getQuoteHtml(q));
    Object.entries(priceMap).forEach(([type, amount]) => {
      const value = Number(amount) || 0;
      if (!(value > 0)) return;
      totals[type] = (Number(totals[type]) || 0) + value;
    });
  });

  return Object.entries(totals).map(([type, amount]) => ({
    type,
    amount: Number(amount) || 0,
  }));
}
